import { createRootRoute, createRoute, createRouter, Outlet } from "@tanstack/react-router";

import { AccessPage } from "./routes/AccessPage";
import { DoorPage } from "./routes/DoorPage";
import { LegacyRedirectPage } from "./routes/LegacyRedirectPage";
import { ShellPage } from "./routes/ShellPage";

/**
 * Code-based routing.
 *
 * Two things are load-bearing here, not cosmetic:
 *
 * **The `/` vs `/app` split.** The Cloudflare Access application **bypasses**
 * `/` so the door (S1) can render to a signed-out visitor, and **protects**
 * `/app` so navigating there is what triggers the Google Workspace sign-in.
 * See `infra/README.md`.
 *
 * **The three-level scheme `/app/:area/:module/:tab`.** Area, module and tab all
 * live in the URL so a teammate can share the exact screen they are looking at
 * (UX v0.3 §3). A view that cannot be linked to cannot be discussed.
 */
const rootRoute = createRootRoute({ component: () => <Outlet /> });

const doorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: DoorPage,
});

const shellRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/app",
  component: ShellPage,
});

// Declared before the `$area` route it would otherwise look like. A static
// segment outranks a dynamic one, so `/app/access` is the access screen and
// never an area whose key happens to be "access".
const accessRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/app/access",
  component: AccessPage,
});

// Also before `$area`: `/app/home` is the neutral home screen the logo resets
// to — no area open, no module loaded — never an area whose key is "home".
const homeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/app/home",
  component: ShellPage,
});

// Also before `$area`, and for the same reason: `/app/legacy/...` is the
// landing the subdomain cut-off redirects to (KET-3492), never an area whose
// key happens to be "legacy". The trailing `$` is the splat that carries the
// ORIGINAL path through — the mapping to a real route is derived from `/v1/me`,
// so a module that adds a tab needs no edge-rule edit.
const legacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/app/legacy/$host/$",
  component: LegacyRedirectPage,
});

// A bare `ofs.ketosource.co/` arrives with no trailing path at all.
const legacyRootRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/app/legacy/$host",
  component: LegacyRedirectPage,
});

const areaRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/app/$area",
  component: ShellPage,
});

const moduleRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/app/$area/$module",
  component: ShellPage,
});

const tabRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/app/$area/$module/$tab",
  component: ShellPage,
});

const routeTree = rootRoute.addChildren([
  doorRoute,
  shellRoute,
  accessRoute,
  homeRoute,
  legacyRoute,
  legacyRootRoute,
  areaRoute,
  moduleRoute,
  tabRoute,
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
